<template>
  <div class="subscription-modal">
    <div class="subscription-modal__content">
      <h2 class="subscription-modal__title">Оформление</h2>
      
      <div class="subscription-modal__info">
        <p class="subscription-modal__period">{{ period }} • до {{ endDate }}</p>
        <p class="subscription-modal__price">{{ price }} руб.</p>
      </div>

      <!-- Лоадер методов оплаты -->
      <div v-if="isLoadingMethods" class="subscription-modal__loading">
        <Loader />
        <p>Загружаем методы оплаты...</p>
      </div>

      <!-- Методы оплаты -->
      <div v-else-if="paymentMethods.length > 0" class="subscription-modal__payment-section">
        <!-- Email input если требуется -->
        <div v-if="shouldShowEmailInput" class="subscription-modal__email-input">
          <label for="email">Email для получения чека:</label>
          <div class="subscription-modal__email-input-wrapper">
            <input 
              id="email"
              v-model="email"
              type="email" 
              placeholder="example@mail.com"
              :class="{ 'error': emailError }"
              :disabled="isUpdatingEmail || isProcessingPayment"
            />
            <div v-if="isUpdatingEmail" class="subscription-modal__email-loading">
              <Loader />
            </div>
          </div>
          <span v-if="emailError" class="error-message">{{ emailError }}</span>
        </div>

        <!-- Список методов оплаты -->
        <div class="subscription-modal__methods-list">
          <button 
            v-for="method in paymentMethods"
            :key="method.id"
            :class="[
              'subscription-modal__method',
              {
                'subscription-modal__method--selected': selectedMethod?.id === method.id,
                'subscription-modal__method--priority': method.is_priority,
                'subscription-modal__method--disabled': !method.is_active || isProcessingPayment
              }
            ]"
            :disabled="!method.is_active || isProcessingPayment"
            @click="selectMethod(method)"
          >
            <span v-if="method.is_priority" class="subscription-modal__priority-badge">
              РЕКОМЕНДУЕТСЯ
            </span>
            {{ getMethodDisplayName(method) }}
            <span v-if="isBalanceMethod(method)" class="subscription-modal__balance-info">
              ({{ userStore.user?.balance || 0 }} руб.)
            </span>
          </button>
        </div>

        <p v-if="paymentErrorMessage" class="subscription-modal__payment-error">
          {{ paymentErrorMessage }}
        </p>
      </div>

      <!-- Ошибка загрузки -->
      <div v-else-if="error" class="subscription-modal__error">
        <p>Ошибка загрузки методов оплаты: {{ error }}</p>
        <button @click="retryLoadMethods" class="subscription-modal__retry-button">
          Попробовать снова
        </button>
      </div>

      <div v-else class="subscription-modal__error">
        <p>Нет доступных способов оплаты для выбранного периода и режима</p>
        <button @click="retryLoadMethods" class="subscription-modal__retry-button">
          Обновить способы оплаты
        </button>
      </div>
    </div>

    <!-- Кнопка оплаты внизу -->
    <div v-if="paymentMethods.length > 0" class="subscription-modal__footer">
      <button 
        class="subscription-modal__pay-button"
        :disabled="!selectedMethod || (shouldShowEmailInput && (!email || !isEmailValid)) || isProcessingPayment || isUpdatingEmail"
        @click="handlePayment"
      >
        <template v-if="isUpdatingEmail">
          Обновляем email...
        </template>
        <template v-else-if="isProcessingPayment">
          <div class="subscription-modal__payment-loader">
            <div class="loader-ring"></div>
            <span>Оплата...</span>
          </div>
        </template>
        <template v-else>
          {{ forceEmailInput ? 'Сохранить и продолжить' : `Оплатить ${price} руб.` }}
        </template>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { usePaymentStore } from '@/entities/Payment/model/paymentStore'
import { useUserStore } from '@/stores/useUser'
import type { PaymentMethod } from '@/entities/Payment/model/types'
import { getPaymentMethodDisplayName } from '@/shared/utils/payment/paymentAliases'
import { useVibration } from '@/shared/utils/system/vibration'
import Loader from '@/ui/components/Loader.vue'

interface Props {
  period?: string
  endDate?: string
  price?: number
  months?: number
  autopaymentEnabled?: boolean
  subscriptionId?: string
  onTinkoffPayment?: () => void
  onYookassaPayment?: () => void
  onPayment?: (method: PaymentMethod, email?: string) => void | Promise<void>
  forceEmailInput?: boolean
  emailErrorMessage?: string
  paymentErrorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  period: '6 месяцев',
  endDate: '31 мая 2025',
  price: 660,
  months: 1,
  autopaymentEnabled: false,
  forceEmailInput: false,
  emailErrorMessage: '',
  paymentErrorMessage: '',
})

const emit = defineEmits<{
  close: []
  tinkoffPayment: []
  yookassaPayment: []
  payment: [method: PaymentMethod, email?: string]
}>()

const paymentStore = usePaymentStore()
const userStore = useUserStore()
const { vibratePattern } = useVibration()

// Email состояние
const email = ref(userStore.user?.email || '')
const emailError = ref('')
const isUpdatingEmail = ref(false)
const isEmailTouchedByUser = ref(false)
const isSyncingEmailFromProfile = ref(false)

// Состояние процесса оплаты
const isProcessingPayment = ref(false)

// Computed свойства
const paymentMethods = computed(() => paymentStore.paymentMethods.filter(m => m.is_active))
const selectedMethod = computed(() => paymentStore.selectedPaymentMethod)
const isLoadingMethods = computed(() => paymentStore.isLoadingMethods)
const error = computed(() => paymentStore.error)
const forceEmailInput = computed(() => props.forceEmailInput)
const paymentErrorMessage = computed(() => props.paymentErrorMessage)
const shouldShowEmailInput = computed(
  () => Boolean(selectedMethod.value?.requires_email) || forceEmailInput.value
)
const normalizedProfileEmail = computed(() =>
  (userStore.user?.email || '').trim().toLowerCase()
)

// Валидация email
const isEmailValid = computed(() => {
  if (!shouldShowEmailInput.value) return true
  if (!email.value) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.value)
})

// Получение отображаемого имени метода оплаты
const getMethodDisplayName = (method: PaymentMethod): string => {
  return getPaymentMethodDisplayName(method.name, method.alias)
}

// Проверка, является ли метод внутренним балансом
const isBalanceMethod = (method: PaymentMethod): boolean => {
  const methodName = method.name?.toLowerCase() || ''
  const methodId = method.id?.toLowerCase() || ''
  return methodName.includes('баланс') || methodName.includes('balance') || methodId.includes('balance')
}

// Наблюдаем за изменением выбранного метода
watch(selectedMethod, (newMethod) => {
  if ((newMethod?.requires_email || forceEmailInput.value) && !email.value) {
    emailError.value = ''
  }
})

watch(email, (newEmail) => {
  if (!isSyncingEmailFromProfile.value) {
    isEmailTouchedByUser.value = true
  }

  if (shouldShowEmailInput.value) {
    if (!newEmail) {
      emailError.value = 'Email обязателен для данного метода оплаты'
    } else if (!isEmailValid.value) {
      emailError.value = 'Введите корректный email'
    } else {
      emailError.value = ''
    }
  }
})

watch(
  () => userStore.user?.email,
  (profileEmail) => {
    if (!profileEmail) {
      return
    }

    if (!isEmailTouchedByUser.value || !email.value) {
      isSyncingEmailFromProfile.value = true
      email.value = profileEmail
      isSyncingEmailFromProfile.value = false
    }
  },
  { immediate: true }
)

watch(
  () => props.emailErrorMessage,
  (message) => {
    if (message) {
      emailError.value = message
    }
  },
  { immediate: true }
)

// Методы
async function selectMethod(method: PaymentMethod) {
  // Добавляем вибрацию при выборе метода оплаты
  await vibratePattern('button')
  
  paymentStore.selectPaymentMethod(method)
}

async function handlePayment() {
  if (!selectedMethod.value) return
  
  // Добавляем вибрацию для кнопки оплаты (критическое действие)
  await vibratePattern('critical')
  
  if (shouldShowEmailInput.value && (!email.value || !isEmailValid.value)) {
    emailError.value = 'Введите корректный email'
    return
  }

  // Предотвращаем двойные запросы
  if (isProcessingPayment.value) {
    return
  }

  isProcessingPayment.value = true

  try {
    const normalizedCurrentEmail = email.value.trim().toLowerCase()
    const shouldUpdateEmail =
      shouldShowEmailInput.value &&
      (forceEmailInput.value ||
        (normalizedCurrentEmail.length > 0 &&
          normalizedCurrentEmail !== normalizedProfileEmail.value))

    if (shouldUpdateEmail) {
      isUpdatingEmail.value = true
      await userStore.updateEmail(email.value.trim())
      isUpdatingEmail.value = false
    }

    const emailToUse = shouldShowEmailInput.value ? email.value.trim() : undefined

    // Вызываем callback если он передан - это старая логика для совместимости
    if (props.onPayment) {
      await props.onPayment(selectedMethod.value, emailToUse)
      return
    }

    // Новая логика оплаты
    emit('payment', selectedMethod.value, emailToUse)
    
  } catch (error) {
    console.error('Ошибка при обработке оплаты:', error)
    if (error && typeof error === 'object' && 'data' in error) {
      const fetchError = error as { data?: { detail?: string } }
      if (fetchError.data?.detail) {
        emailError.value = fetchError.data.detail
      }
    } else {
      emailError.value = 'Ошибка обновления email'
    }

    isUpdatingEmail.value = false
    isProcessingPayment.value = false
  }
}

// Метод для сброса состояния оплаты (может быть вызван извне)
function resetPaymentState() {
  isProcessingPayment.value = false
}

// Expose метод для родительского компонента
defineExpose({
  resetPaymentState,
  isProcessingPayment: () => isProcessingPayment.value
})

async function retryLoadMethods() {
  // Добавляем вибрацию для повторного запроса
  await vibratePattern('button')
  
  try {
    await paymentStore.fetchPaymentMethods({
      months: props.months,
      autopayment_enabled: props.autopaymentEnabled,
      subscription_id: props.subscriptionId,
    })
  } catch (error) {
    console.error('Ошибка повторной загрузки методов оплаты:', error)
  }
}

const handleTinkoffPayment = () => {
  if (props.onTinkoffPayment) {
    props.onTinkoffPayment()
  }
  emit('tinkoffPayment')
}

const handleYookassaPayment = () => {
  if (props.onYookassaPayment) {
    props.onYookassaPayment()
  }
  emit('yookassaPayment')
}

</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/mixins.scss';

.subscription-modal {
  display: flex;
  flex-direction: column;
  min-height: 200px;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    overflow-y: auto;
    justify-content: flex-start;
    padding-top: 20px;
  }

  &__footer {
    margin-top: 24px;
    padding: 0;
    border-top: 1px solid $secondary-200;
    padding-top: 16px;
  }

  &__title {
    @include h1;
    color: $neutral-100;
    margin-bottom: 12px;
  }

  &__info {
    margin-bottom: 40px;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 32px;
    color: $neutral-100;
  }

  &__payment-section {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__payment-methods {
    width: 100%;
    margin-bottom: 32px;
  }

  &__email-input {
    margin-bottom: 24px;
    text-align: left;

    label {
      @include p2-regular;
      color: $neutral-100;
      display: block;
      margin-bottom: 8px;
    }

    &-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    input {
      width: 100%;
      height: 48px;
      padding: 12px 16px;
      border: 2px solid $secondary-200;
      border-radius: 12px;
      background: $secondary-200;
      color: $neutral-100;
      font-size: 16px;
      transition: border-color 0.3s ease;

      &:focus {
        outline: none;
        border-color: $primary-100;
      }

      &.error {
        border-color: #ff4444;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &::placeholder {
        color: $neutral-60;
      }
    }

    .error-message {
      @include p3-regular;
      color: #ff4444;
      display: block;
      margin-top: 4px;
    }
  }

  &__email-loading {
    position: absolute;
    right: 12px;
    display: flex;
    align-items: center;
    
    :deep(.loader) {
      width: 20px;
      height: 20px;
    }
  }

  &__payment-error {
    margin-top: 12px;
    color: #ff4444;
    @include p3-regular;
  }

  &__methods-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    flex: 1;
  }

  &__method {
    position: relative;
    width: 100%;
    height: 48px;
    padding: 15px 20px;
    border: 2px solid $secondary-200;
    border-radius: 12px;
    background: $secondary-200;
    color: $neutral-100;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;

    &:hover:not(:disabled) {
      background: $secondary-200-active;
      transform: translateY(-1px);
    }

    &--selected {
      border-color: $primary-100;
      background: $secondary-200-active;
      color: $primary-100;
    }

    &--priority {
      &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        // background: $primary-gradient;
        border-radius: 14px;
        z-index: -1;
      }
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__priority-badge {
    position: absolute;
    top: -8px;
    right: 12px;
    padding: 2px 8px;
    background: $primary-100;
    color: $neutral-200;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__balance-info {
    color: $neutral-60;
    font-size: 14px;
    // margin-left: 8px;
  }

  &__pay-button {
    @include p1-medium;
    
    width: 100%;
    height: 48px;
    border-radius: 12px;
    padding: 12px 20px;
    border: none;
    background: $primary-gradient;
    color: $neutral-200;
    line-height: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover:not(:disabled) {
      background: $primary-gradient-active;
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }

  &__payment-loader {
    display: flex;
    align-items: center;
    gap: 8px;

    .loader-ring {
      width: 16px;
      height: 16px;
      border: 2px solid transparent;
      border-top-color: $neutral-200;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }

  &__error {
    text-align: center;
    color: $neutral-100;
    
    p {
      margin-bottom: 16px;
      color: #ff4444;
    }
  }

  &__retry-button {
    padding: 8px 16px;
    background: $secondary-200;
    color: $neutral-100;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: $secondary-200-active;
    }
  }

  &__period {
    @include p1-regular;
    color: $neutral-100;
    margin-bottom: 4px;
  }

  &__price {
    @include p2-regular;
    color: $neutral-100;
  }

  &__buttons {
    display: flex;
    gap: 12px;
    width: 100%;
    margin-top: 24px;
  }

  &__button {
    flex: 1;
    height: 56px;
    border-radius: 16px;
    border: none;
    font-family: 'Unbounded', sans-serif;
    font-weight: 600;
    // font-size: 16px;
    line-height: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }
    
    &--back {
      background: $secondary-200;
      color: $neutral-100;
      
      &:hover:not(:disabled) {
        background: $secondary-200-active;
        transform: translateY(-2px);
      }
      
      &:active:not(:disabled) {
        transform: translateY(0);
      }
    }
    
    &--pay {
      background: $primary-gradient;
      color: $neutral-200;
      
      &:hover:not(:disabled) {
        background: $primary-gradient-active;
        transform: translateY(-2px);
      }
      
      &:active:not(:disabled) {
        transform: translateY(0);
      }
    }
  }
}

// @media (max-width: 768px) {
//   .subscription-modal {
//     &__title {
//       font-size: 24px;
//       line-height: 30px;
//       margin-bottom: 24px;
//     }

//     &__price {
//       font-size: 28px;
//       line-height: 34px;
//     }

//     &__info {
//       margin-bottom: 32px;
//     }

//     &__buttons {
//       flex-direction: column;
//       gap: 12px;
//     }

//     &__button {
//       height: 48px;
//       font-size: 14px;
//       line-height: 18px;
//     }
//   }
// }

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
